import { useCallback, useEffect, useMemo, useState } from "react";
import { useOrg } from "../../lib/org/useOrg";
import { useAuth } from "../../lib/auth/useAuth";
import { isSupabaseConfigured, supabase } from "../../lib/supabase";
import { submitInvitation, INVITE_EXPIRY_DAYS } from "../../lib/org/invites";
import {
  createAuditShare,
  generateAuditShareToken,
  revokeAuditShare,
  type AuditShareScope,
} from "../../lib/org/auditShares";
import type { OrgMemberStatus, OrgRole, ReviewMode } from "../../lib/org/types";
import {
  ALL_CAPABILITIES,
  ROLE_DEFAULTS,
  type Capability,
  type Role,
} from "../../lib/permissions/capabilities";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useOrgOverrides } from "../../lib/permissions/useOrgOverrides";
import {
  setOverride,
  clearOverride,
} from "../../lib/permissions/overridesStore";

// ---------------------------------------------------------------------------
// Avatar helpers — reused by SettingsProfileSection and exported for Layout
// ---------------------------------------------------------------------------

// Six-slot brand palette matching VendorLogo's approach.
const AVATAR_PALETTE: ReadonlyArray<string> = [
  "bg-indigo-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-fuchsia-500",
];

// djb2-style stable hash (same algorithm as VendorLogo).
function stringHash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return h < 0 ? ~h : h;
}

/** Returns the initials (up to 2 chars) derived from a display name or email. */
export function avatarInitials(nameOrEmail: string): string {
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return "?";
  // If there's a space (full name), use first letter of first + last word.
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
  }
  // Single word or email: first two chars (upper).
  return trimmed.slice(0, 2).toUpperCase();
}

/** Deterministic Tailwind bg class for an email. */
export function avatarColor(email: string): string {
  return AVATAR_PALETTE[stringHash(email) % AVATAR_PALETTE.length] ?? "bg-indigo-500";
}

interface UserAvatarProps {
  /** Avatar image URL (from user_metadata.avatar_url). May be null/undefined. */
  avatarUrl?: string | null;
  /** Display name or email — used for initials + color hash. */
  nameOrEmail: string;
  /** Email used for hashing the background color. Falls back to nameOrEmail. */
  email?: string;
  /** Pixel size bucket. */
  size?: 24 | 32 | 40 | 48;
  className?: string;
}

const USER_AVATAR_SIZE_CLASSES: Record<
  24 | 32 | 40 | 48,
  { box: string; text: string; pixels: number }
> = {
  24: { box: "w-6 h-6",  text: "text-[10px]", pixels: 24 },
  32: { box: "w-8 h-8",  text: "text-xs",     pixels: 32 },
  40: { box: "w-10 h-10",text: "text-sm",     pixels: 40 },
  48: { box: "w-12 h-12",text: "text-base",   pixels: 48 },
};

/** Circular avatar — shows img if url present, else initials fallback. */
export function UserAvatar({
  avatarUrl,
  nameOrEmail,
  email,
  size = 32,
  className = "",
}: UserAvatarProps) {
  const [imgErrored, setImgErrored] = useState(false);
  const dims = USER_AVATAR_SIZE_CLASSES[size];
  const hashSeed = email ?? nameOrEmail;
  const color = avatarColor(hashSeed);
  const initials = avatarInitials(nameOrEmail);

  if (avatarUrl && !imgErrored) {
    return (
      <img
        src={avatarUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        referrerPolicy="no-referrer"
        width={dims.pixels}
        height={dims.pixels}
        onError={() => setImgErrored(true)}
        className={`${dims.box} rounded-full object-cover border border-slate-200 shrink-0 ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center shrink-0 rounded-full text-white font-semibold select-none ${dims.box} ${dims.text} ${color} ${className}`}
    >
      {initials}
    </span>
  );
}

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

// Well-known fake user id used only in bypass mode to exercise the
// members-table UI surface. Kept stable so tests can target it by testid.
const BYPASS_FAKE_MEMBER_ID = "00000000-0000-0000-0000-0000000000ff";

// ---------------------------------------------------------------------------
// Settings page — org details, members, invitations, reset defaults.
//
// This file is the single landing surface for every org-admin task. The
// three sub-sections are rendered as separate components below but all live
// in this file until one of them outgrows ~300 lines; keeping them co-
// located during early iteration avoids a directory churn while the
// testids and patterns settle.
// ---------------------------------------------------------------------------
export function Settings() {
  const { activeOrg, loading } = useOrg();

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    if (!activeOrg) return "No active organization";
    return `Manage ${activeOrg.name}`;
  }, [loading, activeOrg]);

  // Receipt Forwarding
  const [copied, setCopied] = useState(false);

  // Notifications
  const [slackWebhook, setSlackWebhook] = useState<string>(
    () => localStorage.getItem("proofly.notifications.slackWebhook") ?? ""
  );
  const [slackSaved, setSlackSaved] = useState(false);
  const [emailNotify, setEmailNotify] = useState<boolean>(
    () => localStorage.getItem("proofly.notifications.email") === "true"
  );

  function saveSlackWebhook() {
    localStorage.setItem("proofly.notifications.slackWebhook", slackWebhook);
    setSlackSaved(true);
    setTimeout(() => setSlackSaved(false), 2000);
  }

  return (
    <div data-testid="settings-page" className="max-w-4xl mx-auto space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </header>

      <SettingsProfileSection />
      <SettingsOrgSection />

      {/* Receipt Forwarding */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Receipt Forwarding</h2>
          <p className="text-sm text-slate-500 mt-1">
            Forward any receipt email to this address and Proofly will extract and file it automatically.
          </p>
        </div>
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <span className="flex-1 text-sm font-mono text-slate-800 select-all">
            receipts+{activeOrg?.id ?? "your-org"}@proofly.one
          </span>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(`receipts+${activeOrg?.id ?? ""}@proofly.one`);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="text-xs font-semibold text-[#0070F2] hover:text-[#005BCA] whitespace-nowrap"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <div className="text-xs text-slate-500 space-y-1">
          <p>• Works with Gmail, Outlook, Apple Mail, and any email client</p>
          <p>• Forward directly or set up a filter to auto-forward from specific senders</p>
          <p>• Receipts appear in your Inbox within minutes</p>
        </div>
      </section>

      {/* Notifications */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Notifications</h2>
          <p className="text-sm text-slate-500 mt-1">
            Get notified when expenses are submitted for review or approved.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-slate-700">
            Slack webhook URL
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={slackWebhook}
              onChange={(e) => setSlackWebhook(e.target.value)}
              placeholder="https://hooks.slack.com/services/..."
              className="flex-1 px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
            />
            <button
              type="button"
              onClick={saveSlackWebhook}
              className="px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] text-white text-sm font-medium rounded-xl transition-colors"
            >
              Save
            </button>
          </div>
          {slackSaved && (
            <p className="text-xs text-green-600 font-medium">Webhook saved.</p>
          )}
          <p className="text-xs text-slate-500">
            Create an incoming webhook at api.slack.com/messaging/webhooks and paste the URL here.
            Notifications fire when an expense is submitted or approved.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-slate-700">Email notifications</label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={emailNotify}
              onChange={(e) => {
                setEmailNotify(e.target.checked);
                localStorage.setItem("proofly.notifications.email", String(e.target.checked));
              }}
              className="rounded border-slate-300 text-[#0070F2] focus:ring-[#0070F2]"
            />
            Send email when an expense is submitted for my review
          </label>
        </div>
      </section>

      <SettingsReviewSection />
      <SettingsMembersSection />
      <SettingsPermissionsSection />
      <SettingsInviteSection />
      <SettingsAuditSharesSection />
      <SettingsDangerSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile section — Wave 16. Lets logged-in users view/edit their profile.
// ---------------------------------------------------------------------------

function SettingsProfileSection() {
  const { user } = useAuth();
  const { activeRole } = useOrg();

  const displayName: string =
    (user?.user_metadata?.full_name as string | undefined) ?? "";
  const email: string = user?.email ?? "";
  const avatarUrl: string | undefined =
    user?.user_metadata?.avatar_url as string | undefined;
  const createdAt: string | undefined = user?.created_at;

  const [nameInput, setNameInput] = useState<string>(displayName);
  const [saveStatus, setSaveStatus] = useState<{
    kind: "idle" | "saving" | "saved" | "error";
    message?: string;
  }>({ kind: "idle" });

  // Keep local input in sync if user object changes (e.g. after save).
  useEffect(() => {
    setNameInput(
      (user?.user_metadata?.full_name as string | undefined) ?? "",
    );
  }, [user?.user_metadata?.full_name]);

  async function handleSave() {
    if (!user) return;
    const trimmed = nameInput.trim();
    setSaveStatus({ kind: "saving" });
    try {
      if (E2E_BYPASS) {
        // Bypass: optimistic success — real Supabase not available.
        await new Promise<void>((r) => setTimeout(r, 150));
        setSaveStatus({ kind: "saved", message: "Saved" });
        return;
      }
      if (!isSupabaseConfigured) {
        throw new Error("Supabase is not configured");
      }
      const { error } = await supabase.auth.updateUser({
        data: { full_name: trimmed },
      });
      if (error) throw new Error(error.message);
      setSaveStatus({ kind: "saved", message: "Saved" });
    } catch (err) {
      setSaveStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to save",
      });
    }
  }

  const nameLabel: string = displayName || email;

  return (
    <section
      data-testid="settings-section-profile"
      className="bg-white border border-slate-200 rounded-lg p-6"
    >
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Profile</h2>

      <div className="flex items-start gap-5">
        {/* Avatar */}
        <UserAvatar
          avatarUrl={avatarUrl}
          nameOrEmail={nameLabel}
          email={email}
          size={48}
        />

        <div className="flex-1 space-y-4">
          {/* Display name */}
          <div>
            <label
              htmlFor="profile-display-name"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Display name
            </label>
            <div className="flex items-center gap-3">
              <input
                id="profile-display-name"
                data-testid="profile-display-name"
                type="text"
                value={nameInput}
                onChange={(e) => {
                  setNameInput(e.target.value);
                  if (saveStatus.kind !== "idle") setSaveStatus({ kind: "idle" });
                }}
                disabled={saveStatus.kind === "saving"}
                placeholder="Your full name"
                className="w-full max-w-sm px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0070F2] focus:border-transparent disabled:bg-slate-50"
              />
              <button
                type="button"
                data-testid="profile-save-name"
                onClick={() => { void handleSave(); }}
                disabled={
                  saveStatus.kind === "saving" ||
                  nameInput.trim() === displayName
                }
                className="inline-flex items-center bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {saveStatus.kind === "saving" ? "Saving…" : "Save"}
              </button>
            </div>
            {saveStatus.kind === "saved" && (
              <p
                data-testid="profile-save-status"
                className="mt-1 text-sm text-emerald-600"
              >
                {saveStatus.message}
              </p>
            )}
            {saveStatus.kind === "error" && (
              <p
                data-testid="profile-save-status"
                className="mt-1 text-sm text-red-600"
              >
                {saveStatus.message}
              </p>
            )}
          </div>

          {/* Read-only fields */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">
                Email
              </div>
              <div
                data-testid="profile-email"
                className="text-slate-900 truncate"
              >
                {email || "—"}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                Email changes go through account settings
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">
                Role in active org
              </div>
              <div
                data-testid="profile-role"
                className="text-slate-900 capitalize"
              >
                {activeRole ?? "—"}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">
                Member since
              </div>
              <div data-testid="profile-member-since" className="text-slate-900">
                {formatDate(createdAt)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Audit shares — Wave 11. Admin/owner mints a tokenized read-only share
// link external auditors can visit without signing in.
// ---------------------------------------------------------------------------

const BYPASS_SHARES_KEY = "proofly.bypass.audit_shares.v1";

interface StoredShare {
  id: string;
  name: string;
  expiresAt: string;
  revokedAt: string | null;
  url?: string; // kept only in bypass mode so tests can copy it later
  scope: AuditShareScope;
}

function readBypassShares(): StoredShare[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(BYPASS_SHARES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as StoredShare[];
  } catch { return []; }
}

function writeBypassShares(rows: StoredShare[]): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(BYPASS_SHARES_KEY, JSON.stringify(rows)); }
  catch { /* swallow */ }
}

function defaultExpiryIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function SettingsAuditSharesSection() {
  const { activeOrg, memberships } = useOrg();
  const { user } = useAuth();

  const myRole: OrgRole | null = useMemo(() => {
    if (!activeOrg) return null;
    const m = memberships.find((m) => m.organization.id === activeOrg.id);
    return m?.role ?? null;
  }, [activeOrg, memberships]);

  const canManage = myRole === "owner" || myRole === "admin";

  const [name, setName] = useState("");
  const [expiresOn, setExpiresOn] = useState(defaultExpiryIso());
  const [includeExpenses, setIncludeExpenses] = useState(true);
  const [includeInvoices, setIncludeInvoices] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [shares, setShares] = useState<StoredShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadShares = useCallback(async () => {
    if (!activeOrg) { setShares([]); return; }
    setLoading(true);
    setError(null);
    if (E2E_BYPASS) {
      setShares(readBypassShares());
      setLoading(false);
      return;
    }
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("audit_shares")
      .select("id, name, expires_at, revoked_at, scope_json")
      .eq("organization_id", activeOrg.id)
      .order("created_at", { ascending: false });
    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    const rows = (data ?? []) as Array<{
      id: string;
      name: string;
      expires_at: string;
      revoked_at: string | null;
      scope_json: AuditShareScope;
    }>;
    setShares(rows.map((r) => ({
      id: r.id, name: r.name,
      expiresAt: r.expires_at, revokedAt: r.revoked_at,
      scope: r.scope_json ?? {},
    })));
    setLoading(false);
  }, [activeOrg]);

  useEffect(() => { void loadShares(); }, [loadShares]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!activeOrg || !canManage) return;
    if (!name.trim()) { setError("Name is required"); return; }
    if (!expiresOn) { setError("Expiry date is required"); return; }
    setSubmitting(true);
    setError(null);
    setCreatedUrl(null);
    const expiresAtIso = new Date(`${expiresOn}T23:59:59Z`).toISOString();
    const scope: AuditShareScope = {};
    if (dateFrom) scope.dateFrom = dateFrom;
    if (dateTo) scope.dateTo = dateTo;
    const types: Array<"expenses" | "invoices"> = [];
    if (includeExpenses) types.push("expenses");
    if (includeInvoices) types.push("invoices");
    if (types.length > 0 && types.length < 2) scope.types = types;
    try {
      if (E2E_BYPASS) {
        const { raw } = await generateAuditShareToken();
        const origin = typeof window !== "undefined" ? window.location.origin : "https://proofly.one";
        const url = `${origin}/audit-share/${raw}`;
        const next: StoredShare = {
          id: `bypass-share-${crypto.randomUUID()}`,
          name: name.trim(),
          expiresAt: expiresAtIso,
          revokedAt: null,
          url,
          scope,
        };
        const existing = readBypassShares();
        const rows = [next, ...existing];
        writeBypassShares(rows);
        setShares(rows);
        setCreatedUrl(url);
      } else {
        if (!user) throw new Error("Not signed in");
        const result = await createAuditShare({
          orgId: activeOrg.id,
          name: name.trim(),
          expiresAt: expiresAtIso,
          scope,
        });
        setCreatedUrl(result.url);
        await loadShares();
      }
      setName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create share");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(share: StoredShare): Promise<void> {
    if (!canManage) return;
    const confirmed = window.confirm(`Revoke audit share "${share.name}"?`);
    if (!confirmed) return;
    try {
      if (E2E_BYPASS) {
        const nowIso = new Date().toISOString();
        const next = readBypassShares().map((s) =>
          s.id === share.id ? { ...s, revokedAt: nowIso } : s,
        );
        writeBypassShares(next);
        setShares(next);
      } else {
        await revokeAuditShare(share.id);
        await loadShares();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke share");
    }
  }

  function formatShareDate(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return iso; }
  }

  return (
    <section
      data-testid="audit-shares-section"
      className="bg-white border border-slate-200 rounded-lg p-6"
    >
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Audit shares</h2>

      {!canManage ? (
        <p className="text-sm text-slate-500">Only owners and admins can manage audit shares.</p>
      ) : (
        <>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="audit-share-name" className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                <input
                  id="audit-share-name"
                  data-testid="audit-share-name"
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError(null); }}
                  placeholder="Year-end audit"
                  disabled={submitting}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0070F2] disabled:bg-slate-50"
                />
              </div>
              <div>
                <label htmlFor="audit-share-expires" className="block text-xs font-medium text-slate-600 mb-1">Expires on</label>
                <input
                  id="audit-share-expires"
                  data-testid="audit-share-expires"
                  type="date"
                  value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                  disabled={submitting}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0070F2] disabled:bg-slate-50"
                />
              </div>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-xs font-medium text-slate-600 mb-1">Scope</legend>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    data-testid="audit-share-scope-expenses"
                    checked={includeExpenses}
                    onChange={(e) => setIncludeExpenses(e.target.checked)}
                    disabled={submitting}
                  />
                  Expenses
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    data-testid="audit-share-scope-invoices"
                    checked={includeInvoices}
                    onChange={(e) => setIncludeInvoices(e.target.checked)}
                    disabled={submitting}
                  />
                  Invoices
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Date from</label>
                  <input
                    type="date"
                    data-testid="audit-share-scope-dateFrom"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    disabled={submitting}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm disabled:bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Date to</label>
                  <input
                    type="date"
                    data-testid="audit-share-scope-dateTo"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    disabled={submitting}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm disabled:bg-slate-50"
                  />
                </div>
              </div>
            </fieldset>

            {error && <div className="text-sm text-red-600">{error}</div>}

            <button
              type="submit"
              data-testid="audit-share-create"
              disabled={submitting || !name.trim()}
              className="inline-flex items-center bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {submitting ? "Creating…" : "Create share"}
            </button>

            {createdUrl && (
              <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                <div className="text-xs font-medium text-emerald-800 mb-1">
                  Share created. Copy this URL now — we can't show it again.
                </div>
                <input
                  data-testid="audit-share-url"
                  type="text"
                  value={createdUrl}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full px-2 py-1 text-xs font-mono bg-white border border-emerald-300 rounded"
                />
              </div>
            )}
          </form>

          {/* Existing shares table */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Existing shares</h3>
            {loading ? (
              <div className="text-sm text-slate-500">Loading…</div>
            ) : shares.length === 0 ? (
              <div className="text-sm text-slate-500">No audit shares yet.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Expires</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {shares.map((s) => {
                    const now = Date.now();
                    const expired = new Date(s.expiresAt).getTime() < now;
                    const revoked = !!s.revokedAt;
                    return (
                      <tr key={s.id} data-testid={`audit-share-row-${s.id}`} className="text-slate-800">
                        <td className="py-2 pr-4">{s.name}</td>
                        <td className="py-2 pr-4 text-slate-600">{formatShareDate(s.expiresAt)}</td>
                        <td className="py-2 pr-4">
                          {revoked ? (
                            <span data-testid={`audit-share-revoked-${s.id}`} className="text-red-600">Revoked {formatShareDate(s.revokedAt!)}</span>
                          ) : expired ? (
                            <span className="text-amber-600">Expired</span>
                          ) : (
                            <span className="text-emerald-600">Active</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          <div className="inline-flex gap-2">
                            {s.url && !revoked && !expired && (
                              <button
                                type="button"
                                data-testid={`audit-share-copy-${s.id}`}
                                onClick={() => {
                                  if (s.url && navigator.clipboard?.writeText) {
                                    void navigator.clipboard.writeText(s.url);
                                  }
                                }}
                                className="text-xs text-[#0070F2] hover:underline font-medium"
                              >
                                Copy URL
                              </button>
                            )}
                            {!revoked && (
                              <button
                                type="button"
                                data-testid={`audit-share-revoke-${s.id}`}
                                onClick={() => { void handleRevoke(s); }}
                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Reset defaults — thin wrapper around Wave 6's `seed_my_taxonomy` RPC.
// Gated behind a confirmation dialog because it writes ~10 categories + a
// couple dozen tax-policy rows.
// ---------------------------------------------------------------------------
function SettingsDangerSection() {
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState<{
    kind: "idle" | "running" | "done" | "error";
    message?: string;
  }>({ kind: "idle" });

  async function handleReset() {
    setStatus({ kind: "running" });
    try {
      if (E2E_BYPASS) {
        // Bypass mode: nothing to do beyond acknowledging success so the UI
        // test path stays exercised.
        await new Promise((r) => setTimeout(r, 200));
        setStatus({ kind: "done", message: "Defaults restored" });
      } else {
        if (!isSupabaseConfigured) {
          throw new Error("Supabase is not configured");
        }
        const { error } = await supabase.rpc("seed_my_taxonomy");
        if (error) throw new Error(error.message);
        setStatus({ kind: "done", message: "Defaults restored" });
      }
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Reset failed",
      });
    } finally {
      setConfirming(false);
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-2">
        Reset defaults
      </h2>
      <p className="text-sm text-slate-500 mb-3">
        Re-seed the default categories and tax policies for this organization.
        Existing rows are left alone; only missing defaults are inserted.
      </p>

      {!confirming ? (
        <button
          type="button"
          data-testid="settings-reset-defaults"
          onClick={() => {
            setStatus({ kind: "idle" });
            setConfirming(true);
          }}
          className="inline-flex items-center bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-md text-sm font-medium"
        >
          Reset defaults
        </button>
      ) : (
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="settings-reset-defaults-confirm"
            onClick={handleReset}
            disabled={status.kind === "running"}
            className="inline-flex items-center bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            {status.kind === "running" ? "Resetting…" : "Confirm reset"}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={status.kind === "running"}
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
        </div>
      )}

      {status.kind === "done" && (
        <div className="mt-3 text-sm text-emerald-600">{status.message}</div>
      )}
      {status.kind === "error" && (
        <div className="mt-3 text-sm text-red-600">{status.message}</div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Review mode — Wave 17. Admin/owner can configure how uploads are reviewed.
// ---------------------------------------------------------------------------

interface ReviewModeOption {
  value: ReviewMode;
  label: string;
  description: string;
  comingSoon?: boolean;
}

const REVIEW_MODE_OPTIONS: ReviewModeOption[] = [
  {
    value: "manual",
    label: "Manual review",
    description:
      "Every upload requires an explicit admin or owner approval before it counts.",
  },
  {
    value: "auto_approve",
    label: "Auto approve",
    description:
      "Uploads by employees and members are approved automatically at creation time. Admin and owner uploads are self-approved.",
  },
  {
    value: "coming_soon",
    label: "Coming soon",
    description:
      "A future review mode is in development. Behaves like Manual review in the meantime.",
    comingSoon: true,
  },
];

function SettingsReviewSection() {
  const { activeOrg, memberships, setReviewMode, refresh } = useOrg();

  const myRole: OrgRole | null = useMemo(() => {
    if (!activeOrg) return null;
    const m = memberships.find((m) => m.organization.id === activeOrg.id);
    return m?.role ?? null;
  }, [activeOrg, memberships]);

  const canManage = myRole === "owner" || myRole === "admin";

  const [selected, setSelected] = useState<ReviewMode>(
    activeOrg?.reviewMode ?? "manual",
  );
  const [saveStatus, setSaveStatus] = useState<{
    kind: "idle" | "saving" | "saved" | "error";
    message?: string;
  }>({ kind: "idle" });

  useEffect(() => {
    setSelected(activeOrg?.reviewMode ?? "manual");
    setSaveStatus({ kind: "idle" });
  }, [activeOrg?.id, activeOrg?.reviewMode]);

  async function handleSave() {
    if (!activeOrg || !canManage) return;
    setSaveStatus({ kind: "saving" });
    try {
      await setReviewMode(activeOrg.id, selected);
      await refresh();
      setSaveStatus({ kind: "saved" });
    } catch (err) {
      setSaveStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to save",
      });
    }
  }

  const isDirty = selected !== (activeOrg?.reviewMode ?? "manual");

  return (
    <section
      data-testid="settings-section-review"
      className="bg-white border border-slate-200 rounded-lg p-6"
    >
      <h2 className="text-lg font-semibold text-slate-900 mb-1">
        Review mode
      </h2>
      <p className="text-sm text-slate-500 mb-4">
        Control how expense and invoice uploads are approved for your
        organization.
      </p>

      {!canManage ? (
        <p className="text-sm text-slate-500">
          Only owners and admins can change the review mode.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {REVIEW_MODE_OPTIONS.map((opt) => {
              const isSelected = selected === opt.value;
              return (
                <label
                  key={opt.value}
                  data-testid={`settings-review-mode-${opt.value}`}
                  className={[
                    "relative flex flex-col gap-1.5 p-4 border rounded-lg transition-colors",
                    opt.comingSoon
                      ? "opacity-60 cursor-not-allowed border-slate-200 bg-slate-50"
                      : "cursor-pointer " +
                        (isSelected
                          ? "border-[#0070F2] bg-blue-50 ring-1 ring-[#0070F2]"
                          : "border-slate-200 hover:border-slate-300 bg-white"),
                  ].join(" ")}
                >
                  <input
                    type="radio"
                    name="review-mode"
                    value={opt.value}
                    checked={isSelected}
                    disabled={opt.comingSoon || saveStatus.kind === "saving"}
                    onChange={() => {
                      if (!opt.comingSoon) {
                        setSelected(opt.value);
                        if (saveStatus.kind !== "idle")
                          setSaveStatus({ kind: "idle" });
                      }
                    }}
                    className="sr-only"
                  />
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm font-medium ${isSelected ? "text-[#0070F2]" : "text-slate-900"}`}
                    >
                      {opt.label}
                    </span>
                    {opt.comingSoon && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        Coming soon
                      </span>
                    )}
                    {isSelected && !opt.comingSoon && (
                      <span className="w-4 h-4 rounded-full bg-[#0070F2] flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-white" />
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {opt.description}
                  </p>
                </label>
              );
            })}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              data-testid="settings-review-mode-save"
              onClick={() => {
                void handleSave();
              }}
              disabled={!isDirty || saveStatus.kind === "saving"}
              className="inline-flex items-center bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {saveStatus.kind === "saving" ? "Saving…" : "Save"}
            </button>
            <div
              data-testid="settings-review-mode-status"
              className={`text-sm ${
                saveStatus.kind === "error"
                  ? "text-red-600"
                  : saveStatus.kind === "saved"
                    ? "text-emerald-600"
                    : "text-slate-500"
              }`}
            >
              {saveStatus.kind === "error" && saveStatus.message}
              {saveStatus.kind === "saved" && "Saved"}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Organization details
// ---------------------------------------------------------------------------

function roleBadgeClass(role: OrgRole | null): string {
  switch (role) {
    case "owner":
      return "bg-[#0070F2] text-white";
    case "admin":
      return "bg-indigo-100 text-indigo-800";
    case "accountant":
      return "bg-emerald-100 text-emerald-800";
    case "employee":
      return "bg-slate-100 text-slate-700";
    case "auditor":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-slate-100 text-slate-500";
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function SettingsOrgSection() {
  const { activeOrg, memberships, setOrgName } = useOrg();
  const { user } = useAuth();

  // Current user's membership (role) within the active org. In bypass this
  // is always 'owner'; in real mode it comes straight from the memberships
  // list we already fetched.
  const myRole: OrgRole | null = useMemo(() => {
    if (!activeOrg) return null;
    const m = memberships.find((m) => m.organization.id === activeOrg.id);
    return m?.role ?? null;
  }, [activeOrg, memberships]);

  const canEdit = myRole === "owner" || myRole === "admin";

  const [name, setName] = useState<string>(activeOrg?.name ?? "");
  const [status, setStatus] = useState<{
    kind: "idle" | "saving" | "saved" | "error";
    message?: string;
  }>({ kind: "idle" });

  useEffect(() => {
    setName(activeOrg?.name ?? "");
    setStatus({ kind: "idle" });
  }, [activeOrg?.id, activeOrg?.name]);

  async function handleSave() {
    if (!activeOrg) return;
    if (!name.trim()) {
      setStatus({ kind: "error", message: "Name is required" });
      return;
    }
    setStatus({ kind: "saving" });
    try {
      await setOrgName(activeOrg.id, name.trim());
      setStatus({ kind: "saved", message: "Saved" });
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to save",
      });
    }
  }

  // If the current user is the org owner we can show their own email for
  // the "Owner" field. Otherwise we just show the owner user id as a
  // placeholder — the members RPC would have the email but that's lazy-
  // loaded below.
  const ownerEmailFallback =
    activeOrg && user && activeOrg.ownerId === user.id ? user.email ?? null : null;

  return (
    <section
      data-testid="settings-section-org"
      className="bg-white border border-slate-200 rounded-lg p-6"
    >
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        Organization
      </h2>

      {!activeOrg ? (
        <p className="text-sm text-slate-500">No active organization.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <label
                htmlFor="settings-org-name"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                Name
              </label>
              <input
                id="settings-org-name"
                data-testid="settings-org-name-input"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (status.kind !== "idle") setStatus({ kind: "idle" });
                }}
                disabled={!canEdit || status.kind === "saving"}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0070F2] focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div className="pt-5">
              <span
                data-testid="settings-org-role-badge"
                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${roleBadgeClass(myRole)}`}
              >
                {myRole ?? "—"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">
                Created
              </div>
              <div className="text-slate-900">
                {formatDate(activeOrg.createdAt)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">
                Owner
              </div>
              <div className="text-slate-900">
                {ownerEmailFallback ?? activeOrg.ownerId}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              data-testid="settings-org-save"
              onClick={handleSave}
              disabled={
                !canEdit ||
                status.kind === "saving" ||
                name.trim() === activeOrg.name ||
                name.trim() === ""
              }
              className="inline-flex items-center bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {status.kind === "saving" ? "Saving…" : "Save"}
            </button>
            <div
              data-testid="settings-org-save-status"
              className={`text-sm ${
                status.kind === "error"
                  ? "text-red-600"
                  : status.kind === "saved"
                    ? "text-emerald-600"
                    : "text-slate-500"
              }`}
            >
              {status.kind === "error" && status.message}
              {status.kind === "saved" && "Saved"}
            </div>
          </div>
          {!canEdit && (
            <p className="text-xs text-slate-500">
              Only owners and admins can edit organization details.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Members table
// ---------------------------------------------------------------------------

interface MemberRow {
  userId: string;
  email: string;
  role: OrgRole;
  status: OrgMemberStatus;
  joinedAt: string | null;
}

const ROLE_OPTIONS: OrgRole[] = [
  "owner",
  "admin",
  "accountant",
  "employee",
  "auditor",
];

function SettingsMembersSection() {
  const { activeOrg, memberships, refresh } = useOrg();
  const { user } = useAuth();

  const myRole: OrgRole | null = useMemo(() => {
    if (!activeOrg) return null;
    const m = memberships.find((m) => m.organization.id === activeOrg.id);
    return m?.role ?? null;
  }, [activeOrg, memberships]);

  const canManage = myRole === "owner" || myRole === "admin";
  const isOwner = myRole === "owner";

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    if (!activeOrg) {
      setMembers([]);
      return;
    }
    setLoadingMembers(true);
    setError(null);

    if (E2E_BYPASS) {
      // Synthesize: current user + a hardcoded fake employee so the UI
      // surface (role editor, remove button) has something to exercise.
      const rows: MemberRow[] = [
        {
          userId: user?.id ?? "00000000-0000-0000-0000-000000000000",
          email: user?.email ?? "you@example.com",
          role: myRole ?? "owner",
          status: "active",
          joinedAt: activeOrg.createdAt,
        },
        {
          userId: BYPASS_FAKE_MEMBER_ID,
          email: "employee@example.com",
          role: "employee",
          status: "active",
          joinedAt: activeOrg.createdAt,
        },
      ];
      setMembers(rows);
      setLoadingMembers(false);
      return;
    }

    if (!isSupabaseConfigured) {
      setError("Supabase is not configured");
      setLoadingMembers(false);
      return;
    }

    const { data, error } = await supabase.rpc("org_members_with_emails", {
      target_org: activeOrg.id,
    });
    if (error) {
      setError(error.message);
      setMembers([]);
      setLoadingMembers(false);
      return;
    }
    const rows = (data ?? []) as Array<{
      user_id: string;
      email: string | null;
      role: OrgRole;
      status: OrgMemberStatus;
      joined_at: string | null;
    }>;
    setMembers(
      rows.map((r) => ({
        userId: r.user_id,
        email: r.email ?? "—",
        role: r.role,
        status: r.status,
        joinedAt: r.joined_at,
      })),
    );
    setLoadingMembers(false);
  }, [activeOrg, myRole, user?.id, user?.email]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  // Count active owners — we gate "leave org" and "change role" on keeping
  // at least one owner around.
  const activeOwnerCount = useMemo(
    () => members.filter((m) => m.role === "owner" && m.status === "active").length,
    [members],
  );

  const selfUserId = user?.id ?? null;

  async function handleRoleChange(row: MemberRow, next: OrgRole) {
    if (!activeOrg) return;
    if (E2E_BYPASS) {
      setMembers((prev) =>
        prev.map((m) => (m.userId === row.userId ? { ...m, role: next } : m)),
      );
      return;
    }
    const { error } = await supabase
      .from("org_members")
      .update({ role: next })
      .eq("organization_id", activeOrg.id)
      .eq("user_id", row.userId);
    if (error) {
      setError(error.message);
      return;
    }
    await loadMembers();
  }

  async function handleRemove(row: MemberRow) {
    if (!activeOrg) return;
    const confirmed = window.confirm(
      `Remove ${row.email} from ${activeOrg.name}?`,
    );
    if (!confirmed) return;
    if (E2E_BYPASS) {
      setMembers((prev) => prev.filter((m) => m.userId !== row.userId));
      return;
    }
    const { error } = await supabase
      .from("org_members")
      .delete()
      .eq("organization_id", activeOrg.id)
      .eq("user_id", row.userId);
    if (error) {
      setError(error.message);
      return;
    }
    await loadMembers();
  }

  async function handleLeave() {
    if (!activeOrg || !selfUserId) return;
    const confirmed = window.confirm(
      `Leave ${activeOrg.name}? You will lose access to its data.`,
    );
    if (!confirmed) return;
    if (E2E_BYPASS) {
      // Bypass doesn't implement a real "leave" flow; just refresh.
      await refresh();
      return;
    }
    const { error } = await supabase
      .from("org_members")
      .delete()
      .eq("organization_id", activeOrg.id)
      .eq("user_id", selfUserId);
    if (error) {
      setError(error.message);
      return;
    }
    await refresh();
  }

  const canShowLeave =
    selfUserId != null &&
    !(myRole === "owner" && activeOwnerCount <= 1);

  return (
    <section
      data-testid="settings-section-members"
      className="bg-white border border-slate-200 rounded-lg p-6"
    >
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Members</h2>

      {error && (
        <div className="mb-3 text-sm text-red-600" role="alert">
          {error}
        </div>
      )}

      {loadingMembers ? (
        <div className="text-sm text-slate-500">Loading members…</div>
      ) : (
        <div className="overflow-x-auto">
          <table
            data-testid="settings-members-table"
            className="min-w-full text-sm"
          >
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Joined</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((row) => {
                const isSelf = row.userId === selfUserId;
                const isRowOwner = row.role === "owner";
                const canEditThisRole =
                  isOwner && !isSelf && !isRowOwner;
                const canRemoveThisRow =
                  canManage && !isSelf && !isRowOwner;

                return (
                  <tr
                    key={row.userId}
                    data-testid={`settings-member-row-${row.userId}`}
                    className="text-slate-800"
                  >
                    <td className="py-2 pr-4">{row.email}</td>
                    <td className="py-2 pr-4">
                      {canEditThisRole ? (
                        <select
                          data-testid={`settings-member-role-${row.userId}`}
                          value={row.role}
                          onChange={(e) =>
                            handleRoleChange(row, e.target.value as OrgRole)
                          }
                          className="px-2 py-1 border border-slate-300 rounded-md text-sm"
                        >
                          {ROLE_OPTIONS.filter((r) => r !== "owner").map(
                            (r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <span
                          data-testid={`settings-member-role-${row.userId}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roleBadgeClass(row.role)}`}
                        >
                          {row.role}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{row.status}</td>
                    <td className="py-2 pr-4 text-slate-600">
                      {formatDate(row.joinedAt)}
                    </td>
                    <td className="py-2 pr-4">
                      {canRemoveThisRow ? (
                        <button
                          type="button"
                          data-testid={`settings-member-remove-${row.userId}`}
                          onClick={() => handleRemove(row)}
                          className="text-red-600 hover:text-red-800 text-xs font-medium"
                        >
                          Remove
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {members.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-4 text-center text-sm text-slate-500"
                  >
                    No members.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {canShowLeave && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button
            type="button"
            data-testid="settings-leave-org"
            onClick={handleLeave}
            className="text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Leave organization
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Invite form + pending invitations
// ---------------------------------------------------------------------------

const INVITE_ROLE_OPTIONS: OrgRole[] = [
  "admin",
  "accountant",
  "employee",
  "auditor",
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PendingInvite {
  id: string;
  email: string;
  role: OrgRole;
  expiresAt: string;
}

const BYPASS_PENDING_KEY = "proofly.bypass.pending_invites.v1";

function readBypassPending(): PendingInvite[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(BYPASS_PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as PendingInvite[];
  } catch {
    return [];
  }
}

function writeBypassPending(rows: PendingInvite[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(BYPASS_PENDING_KEY, JSON.stringify(rows));
  } catch {
    /* swallow */
  }
}

function SettingsInviteSection() {
  const { activeOrg, memberships } = useOrg();
  const { user } = useAuth();

  const myRole: OrgRole | null = useMemo(() => {
    if (!activeOrg) return null;
    const m = memberships.find((m) => m.organization.id === activeOrg.id);
    return m?.role ?? null;
  }, [activeOrg, memberships]);

  const canInvite = myRole === "owner" || myRole === "admin";

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("employee");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);

  const emailValid = EMAIL_REGEX.test(email.trim());
  const canSubmit = canInvite && emailValid && !submitting && !!activeOrg;

  const loadPending = useCallback(async () => {
    if (!activeOrg) {
      setPending([]);
      return;
    }
    setLoadingPending(true);
    if (E2E_BYPASS) {
      const all = readBypassPending();
      // Filter by active org in case tests put invites on multiple orgs.
      setPending(all);
      setLoadingPending(false);
      return;
    }
    if (!isSupabaseConfigured) {
      setLoadingPending(false);
      return;
    }
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from("org_invitations")
      .select("id, email, role, expires_at")
      .eq("organization_id", activeOrg.id)
      .is("accepted_at", null)
      .gt("expires_at", nowIso)
      .order("created_at", { ascending: false });
    if (error) {
      setError(error.message);
      setLoadingPending(false);
      return;
    }
    const rows = (data ?? []) as Array<{
      id: string;
      email: string;
      role: OrgRole;
      expires_at: string;
    }>;
    setPending(
      rows.map((r) => ({
        id: r.id,
        email: r.email,
        role: r.role,
        expiresAt: r.expires_at,
      })),
    );
    setLoadingPending(false);
  }, [activeOrg]);

  useEffect(() => {
    void loadPending();
  }, [loadPending]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !activeOrg) return;
    setSubmitting(true);
    setError(null);
    setInviteUrl(null);
    const normalized = email.trim().toLowerCase();
    try {
      if (E2E_BYPASS) {
        // Simulate success without hitting Supabase.
        const fakeId = `bypass-inv-${crypto.randomUUID()}`;
        const expiresAt = new Date(
          Date.now() + INVITE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
        ).toISOString();
        const fakeRaw = crypto.randomUUID().replace(/-/g, "");
        const origin =
          typeof window !== "undefined"
            ? window.location.origin
            : "https://proofly.one";
        const url = `${origin}/auth/callback?invite=${fakeRaw}`;
        const next: PendingInvite[] = [
          ...readBypassPending(),
          { id: fakeId, email: normalized, role, expiresAt },
        ];
        writeBypassPending(next);
        setPending(next);
        setInviteUrl(url);
      } else {
        if (!user) throw new Error("Not signed in");
        const result = await submitInvitation({
          email: normalized,
          role,
          orgId: activeOrg.id,
          inviterId: user.id,
        });
        setInviteUrl(result.url);
        await loadPending();
      }
      setEmail("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send invite");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(inv: PendingInvite) {
    if (!activeOrg) return;
    if (E2E_BYPASS) {
      const next = readBypassPending().filter((p) => p.id !== inv.id);
      writeBypassPending(next);
      setPending(next);
      return;
    }
    const { error } = await supabase
      .from("org_invitations")
      .delete()
      .eq("id", inv.id);
    if (error) {
      setError(error.message);
      return;
    }
    await loadPending();
  }

  return (
    <section
      data-testid="settings-section-invite"
      className="bg-white border border-slate-200 rounded-lg p-6"
    >
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        Invite a teammate
      </h2>

      {!canInvite && (
        <p className="text-sm text-slate-500 mb-3">
          Only owners and admins can invite teammates.
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label
              htmlFor="settings-invite-email"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Email
            </label>
            <input
              id="settings-invite-email"
              data-testid="settings-invite-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              disabled={!canInvite || submitting}
              placeholder="teammate@example.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0070F2] focus:border-transparent disabled:bg-slate-50"
            />
          </div>
          <div className="sm:w-48">
            <label
              htmlFor="settings-invite-role"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Role
            </label>
            <select
              id="settings-invite-role"
              data-testid="settings-invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as OrgRole)}
              disabled={!canInvite || submitting}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0070F2] focus:border-transparent disabled:bg-slate-50"
            >
              {INVITE_ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>

        {email.length > 0 && !emailValid && (
          <div
            data-testid="settings-invite-error"
            className="text-sm text-red-600"
          >
            Enter a valid email address.
          </div>
        )}
        {error && (
          <div
            data-testid="settings-invite-error"
            className="text-sm text-red-600"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          data-testid="settings-invite-submit"
          disabled={!canSubmit}
          className="inline-flex items-center bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          {submitting ? "Sending…" : "Send invite"}
        </button>

        {inviteUrl && (
          <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
            <div className="text-xs font-medium text-emerald-800 mb-1">
              Invite sent. If email delivery fails, share this URL directly:
            </div>
            <input
              data-testid="settings-invite-url"
              type="text"
              value={inviteUrl}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              className="w-full px-2 py-1 text-xs font-mono bg-white border border-emerald-300 rounded"
            />
          </div>
        )}
      </form>

      {/* Pending invitations */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">
          Pending invitations
        </h3>
        {loadingPending ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : pending.length === 0 ? (
          <div
            data-testid="settings-pending-invites"
            className="text-sm text-slate-500"
          >
            No pending invitations.
          </div>
        ) : (
          <table
            data-testid="settings-pending-invites"
            className="min-w-full text-sm"
          >
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Expires</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pending.map((inv) => (
                <tr
                  key={inv.id}
                  data-testid={`settings-pending-invite-row-${inv.id}`}
                  className="text-slate-800"
                >
                  <td className="py-2 pr-4">{inv.email}</td>
                  <td className="py-2 pr-4">{inv.role}</td>
                  <td className="py-2 pr-4 text-slate-600">
                    {formatDate(inv.expiresAt)}
                  </td>
                  <td className="py-2 pr-4">
                    <button
                      type="button"
                      data-testid={`settings-revoke-invite-${inv.id}`}
                      onClick={() => handleRevoke(inv)}
                      className="text-red-600 hover:text-red-800 text-xs font-medium"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Permissions matrix — Wave 23. Members x capabilities grid with a three-
// state cell (default / granted / revoked). Admins grant or revoke per-
// member overrides; wave-19 merge semantics take effect immediately for
// whoever is viewing the hub.
// ---------------------------------------------------------------------------

const BYPASS_MEMBERS_KEY = "proofly.bypass.members.v1";

interface BypassMemberRow {
  userId: string;
  email: string;
  role: OrgRole;
  status?: OrgMemberStatus;
  joinedAt?: string | null;
}

interface PermissionsMember {
  userId: string;
  email: string;
  role: OrgRole;
}

function readBypassMembers(): BypassMemberRow[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(BYPASS_MEMBERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as BypassMemberRow[];
  } catch {
    return [];
  }
}

// Capability grouping for the matrix columns. Headings are prefix-based
// heuristics: `view:*` -> View, `write:*` -> Write, `read:*` -> Read-only,
// `approve:*` + `export:*` -> Actions, `manage:*` -> Admin. Every current
// Capability hits exactly one of these; unknown prefixes fall through to
// Admin as a safety default.
type CapabilityGroupLabel = "View" | "Write" | "Read-only" | "Actions" | "Admin";

function groupForCapability(cap: Capability): CapabilityGroupLabel {
  if (cap.startsWith("view:")) return "View";
  if (cap.startsWith("write:")) return "Write";
  if (cap.startsWith("read:")) return "Read-only";
  if (cap.startsWith("approve:") || cap.startsWith("export:")) return "Actions";
  return "Admin"; // manage:* + anything future
}

const CAPABILITY_GROUPS: ReadonlyArray<{
  label: CapabilityGroupLabel;
  capabilities: ReadonlyArray<Capability>;
}> = (() => {
  const order: CapabilityGroupLabel[] = [
    "View",
    "Write",
    "Read-only",
    "Actions",
    "Admin",
  ];
  const buckets = new Map<CapabilityGroupLabel, Capability[]>();
  for (const label of order) buckets.set(label, []);
  for (const cap of ALL_CAPABILITIES) {
    const bucket = buckets.get(groupForCapability(cap));
    if (bucket) bucket.push(cap);
  }
  return order.map((label) => ({
    label,
    capabilities: buckets.get(label) ?? [],
  }));
})();

type CellState = "default" | "granted" | "revoked";

function cycleState(s: CellState): CellState {
  if (s === "default") return "granted";
  if (s === "granted") return "revoked";
  return "default";
}

function cellClass(state: CellState, disabled: boolean): string {
  const base =
    "inline-flex items-center justify-center w-full px-2 py-1 rounded text-[11px] font-medium transition-colors";
  if (disabled) {
    return `${base} bg-slate-50 text-slate-300 cursor-not-allowed border border-slate-100`;
  }
  switch (state) {
    case "granted":
      return `${base} bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-200`;
    case "revoked":
      return `${base} bg-rose-100 text-rose-800 hover:bg-rose-200 border border-rose-200`;
    default:
      return `${base} bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200`;
  }
}

function cellLabel(state: CellState): string {
  if (state === "granted") return "granted";
  if (state === "revoked") return "revoked";
  return "default";
}

function SettingsPermissionsSection() {
  const { activeOrg, memberships, refreshCapabilityOverrides } = useOrg();
  const { user } = useAuth();
  const { can } = useCapabilities();

  const myRole: OrgRole | null = useMemo(() => {
    if (!activeOrg) return null;
    const m = memberships.find((m) => m.organization.id === activeOrg.id);
    return m?.role ?? null;
  }, [activeOrg, memberships]);

  // UI gate: ROLE_DEFAULTS grants `manage:members` to owner + admin in
  // today's role map (accountant doesn't get it). The wave-19 RLS is the
  // real enforcement; this is just polish.
  const canManage = can("manage:members");
  const isOwner = myRole === "owner";
  const selfUserId = user?.id ?? null;

  // Load all org overrides via the wave-23 hook.
  const { overrides: allOverrides, refresh: refreshOrgOverrides } =
    useOrgOverrides(activeOrg?.id ?? null);

  // Members source: real mode uses the same RPC as SettingsMembersSection;
  // bypass mode tries `proofly.bypass.members.v1` first, else falls back
  // to the stock self + fake employee shape so existing tests don't break.
  const [members, setMembers] = useState<PermissionsMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    if (!activeOrg) {
      setMembers([]);
      return;
    }
    setMembersLoading(true);
    setMembersError(null);

    if (E2E_BYPASS) {
      const seeded = readBypassMembers();
      if (seeded.length > 0) {
        setMembers(
          seeded.map((r) => ({
            userId: r.userId,
            email: r.email,
            role: r.role,
          })),
        );
      } else {
        setMembers([
          {
            userId: user?.id ?? "00000000-0000-0000-0000-000000000000",
            email: user?.email ?? "you@example.com",
            role: myRole ?? "owner",
          },
          {
            userId: BYPASS_FAKE_MEMBER_ID,
            email: "employee@example.com",
            role: "employee",
          },
        ]);
      }
      setMembersLoading(false);
      return;
    }

    if (!isSupabaseConfigured) {
      setMembersError("Supabase is not configured");
      setMembersLoading(false);
      return;
    }

    const { data, error } = await supabase.rpc("org_members_with_emails", {
      target_org: activeOrg.id,
    });
    if (error) {
      setMembersError(error.message);
      setMembers([]);
      setMembersLoading(false);
      return;
    }
    const rows = (data ?? []) as Array<{
      user_id: string;
      email: string | null;
      role: OrgRole;
    }>;
    setMembers(
      rows.map((r) => ({
        userId: r.user_id,
        email: r.email ?? "—",
        role: r.role,
      })),
    );
    setMembersLoading(false);
  }, [activeOrg, myRole, user?.id, user?.email]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  // Index overrides by (userId, capability) for O(1) cell lookup.
  const overrideIndex = useMemo(() => {
    const idx = new Map<string, boolean>();
    for (const ov of allOverrides) {
      idx.set(`${ov.userId}|${ov.capability}`, ov.granted);
    }
    return idx;
  }, [allOverrides]);

  function stateFor(userId: string, capability: Capability): CellState {
    const v = overrideIndex.get(`${userId}|${capability}`);
    if (v === undefined) return "default";
    return v ? "granted" : "revoked";
  }

  // Per-row override count badge.
  const overrideCountByUser = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ov of allOverrides) {
      counts.set(ov.userId, (counts.get(ov.userId) ?? 0) + 1);
    }
    return counts;
  }, [allOverrides]);

  const [cellError, setCellError] = useState<string | null>(null);

  async function handleCellClick(
    member: PermissionsMember,
    capability: Capability,
  ): Promise<void> {
    if (!activeOrg) return;
    const current = stateFor(member.userId, capability);
    const next = cycleState(current);
    setCellError(null);
    try {
      if (next === "default") {
        await clearOverride(activeOrg.id, member.userId, capability);
      } else {
        await setOverride(
          activeOrg.id,
          member.userId,
          capability,
          next === "granted",
        );
      }
      await refreshOrgOverrides();
      // Wave 19 — also refresh the viewer's own override set, in case
      // the matrix edited the viewer's row (shouldn't happen per UI
      // gate, but harmless) so `useCapabilities()` reflects the change
      // immediately.
      await refreshCapabilityOverrides();
    } catch (err) {
      setCellError(
        err instanceof Error ? err.message : "Failed to update override",
      );
    }
  }

  async function handleResetCell(
    member: PermissionsMember,
    capability: Capability,
  ): Promise<void> {
    if (!activeOrg) return;
    setCellError(null);
    try {
      await clearOverride(activeOrg.id, member.userId, capability);
      await refreshOrgOverrides();
      await refreshCapabilityOverrides();
    } catch (err) {
      setCellError(
        err instanceof Error ? err.message : "Failed to reset override",
      );
    }
  }

  return (
    <section
      data-testid="settings-permissions-section"
      className="bg-white border border-slate-200 rounded-lg p-6"
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Permissions</h2>
        <p className="text-sm text-slate-500 mt-1">
          Grant or revoke individual capabilities per member. Click a cell to
          cycle default → granted → revoked. Right-click to reset to default.
        </p>
      </div>

      {!canManage ? (
        <p className="text-sm text-slate-500">
          Only owners and admins can manage permissions.
        </p>
      ) : !activeOrg ? (
        <p className="text-sm text-slate-500">No active organization.</p>
      ) : membersLoading ? (
        <div className="text-sm text-slate-500">Loading members…</div>
      ) : (
        <>
          {cellError && (
            <div
              role="alert"
              data-testid="permissions-cell-error"
              className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            >
              {cellError}
            </div>
          )}
          {membersError && (
            <div role="alert" className="mb-3 text-sm text-red-600">
              {membersError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white text-left py-2 pr-3 text-[11px] font-medium text-slate-500 uppercase tracking-wide align-bottom">
                    Member
                  </th>
                  {CAPABILITY_GROUPS.map((group) =>
                    group.capabilities.length === 0 ? null : (
                      <th
                        key={`group-head-${group.label}`}
                        colSpan={group.capabilities.length}
                        className="text-center py-1 px-2 text-[10px] font-semibold text-slate-600 uppercase tracking-wide border-b border-slate-200"
                      >
                        {group.label}
                      </th>
                    ),
                  )}
                  <th className="py-2 pl-2 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wide align-bottom">
                    Overrides
                  </th>
                </tr>
                <tr>
                  <th className="sticky left-0 z-10 bg-white text-left py-2 pr-3 text-[11px] font-medium text-slate-500 align-bottom">
                    &nbsp;
                  </th>
                  {CAPABILITY_GROUPS.flatMap((group) =>
                    group.capabilities.map((cap) => (
                      <th
                        key={`col-head-${cap}`}
                        title={cap}
                        className="py-2 px-1 text-[10px] font-mono text-slate-500 whitespace-nowrap"
                      >
                        {cap}
                      </th>
                    )),
                  )}
                  <th className="py-2 pl-2 text-right">&nbsp;</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((m) => {
                  const isSelf = m.userId === selfUserId;
                  const isRowOwner = m.role === "owner";
                  // Admin rows: only the current owner can edit; other
                  // admins/accountants see a read-only row.
                  const isRowAdminish =
                    m.role === "admin" || m.role === "accountant";
                  const rowReadOnly =
                    isSelf ||
                    isRowOwner ||
                    (isRowAdminish && !isOwner);
                  const badgeLabel = isRowOwner
                    ? "owner-only"
                    : isSelf
                      ? "you"
                      : isRowAdminish && !isOwner
                        ? "owner-only"
                        : null;
                  const count = overrideCountByUser.get(m.userId) ?? 0;
                  return (
                    <tr
                      key={m.userId}
                      data-testid={`permissions-row-${m.userId}`}
                      data-role={m.role}
                      data-readonly={rowReadOnly ? "true" : "false"}
                      className="text-slate-800"
                    >
                      <td className="sticky left-0 z-10 bg-white py-2 pr-3 align-middle">
                        <div className="flex flex-col">
                          <span className="text-[12px] font-medium text-slate-900 truncate max-w-[200px]">
                            {m.email}
                          </span>
                          <span className="flex items-center gap-1.5 mt-0.5">
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${roleBadgeClass(m.role)}`}
                            >
                              {m.role}
                            </span>
                            {badgeLabel && (
                              <span
                                data-testid={`permissions-row-badge-${m.userId}`}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600"
                              >
                                {badgeLabel}
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      {CAPABILITY_GROUPS.flatMap((group) =>
                        group.capabilities.map((cap) => {
                          const state = stateFor(m.userId, cap);
                          // Owners always effectively have every cap via
                          // mergeCapabilities short-circuit; cells are
                          // disabled so admins don't waste a click.
                          const disabled = rowReadOnly || isRowOwner;
                          const roleDefault = ROLE_DEFAULTS[
                            m.role as Role
                          ].includes(cap);
                          const tooltip = `${cap}\nRole default: ${roleDefault ? "granted" : "not granted"}\nCurrent: ${state}`;
                          return (
                            <td
                              key={`${m.userId}-${cap}`}
                              className="py-1 px-0.5 align-middle"
                            >
                              <button
                                type="button"
                                title={tooltip}
                                data-testid={`permissions-cell-${m.userId}-${cap}`}
                                data-state={state}
                                disabled={disabled}
                                onClick={() => {
                                  if (disabled) return;
                                  void handleCellClick(m, cap);
                                }}
                                onContextMenu={(e) => {
                                  if (disabled) return;
                                  e.preventDefault();
                                  void handleResetCell(m, cap);
                                }}
                                className={cellClass(state, disabled)}
                              >
                                <span
                                  data-testid={`permissions-state-${m.userId}-${cap}`}
                                >
                                  {cellLabel(state)}
                                </span>
                              </button>
                            </td>
                          );
                        }),
                      )}
                      <td className="py-2 pl-2 text-right align-middle">
                        <span
                          data-testid={`permissions-override-count-${m.userId}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${count > 0 ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-500"}`}
                        >
                          {count} {count === 1 ? "override" : "overrides"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {members.length === 0 && (
                  <tr>
                    <td
                      colSpan={ALL_CAPABILITIES.length + 2}
                      className="py-4 text-center text-sm text-slate-500"
                    >
                      No members.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-[11px] text-slate-500">
            Owners always retain every capability — overrides are ignored for
            them at the merge layer. Admin rows can only be edited by the org
            owner.
          </p>
        </>
      )}
    </section>
  );
}
